import React, { useRef, useState } from 'react';
import {
  Grid,
  IconButton,
  makeStyles,
  colors,
  Checkbox,
  withStyles,
  Icon,
  Divider,
  Button,
  Typography,
} from '@material-ui/core';
import { Edit, AccessTime, People } from '@material-ui/icons';

import useIsUpMD from 'hooks/media_query/useIsUpMD';

import Title from 'components/typography/Title';
import Description from 'components/typography/Description';

import IngredientsCard from 'components/recipes/steps/ingredients/ingredients_card/IngredientsCard';
import DirectionCard from 'components/recipes/steps/directions/direction_card/DirectionCard';

import LikeButton from 'components/buttons/LikeButton';

import Step from 'components/stepper/Step';
import BookmarkButton from 'components/buttons/BookmarkButton';

const useStyles = makeStyles({
  title: {
    color: colors.red[400],
  },
  button: {
    color: colors.red[400],
    '&:hover': {
      backgroundColor: colors.red[100],
    },
  },
  editButton: {
    color: colors.red[400],
    '&:hover': {
      backgroundColor: colors.red[100],
    },
  },
  coverPhoto: {
    height: 300,
    objectFit: 'fill',
    color: colors.grey[50],
    borderRadius: '8px',
    boxShadow: '0px 1px 4px 2px rgba(0,0,0,0.24)',
  },
  description: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
  },
});

const StyledCheckbox = withStyles({
  colorSecondary: {
    '&.Mui-checked': {
      color: colors.red[400],
    },
  },
})(Checkbox);

const RecipeLoaded = ({
  recipeModel,
  recipeLiked,
  recipeBookmarked,
  exitPage,
  isRecipeOwnedByUser,
  showBookmarkIcon,
  goToEditPage,
}) => {
  const { id, content, userName } = recipeModel;
  const {
    additionalData,
    timeToPrepare,
    title,
    ingredients,
    directions,
  } = content;
  const {
    description,
    coverPhotoURL,
    servings,
    nutricionalData,
  } = additionalData;
  const classes = useStyles();

  const nutricionalTableRef = useRef(null);
  const [hasBeenLiked, setHasBeenLiked] = useState(recipeLiked);
  const [likes, setLikes] = useState(recipeModel.likes);
  const [bookmarked, setBookmarked] = useState(recipeBookmarked);

  const _onLikedChanged = (wasLiked) => {
    if (wasLiked) setLikes(likes + 1);
    else setLikes(likes - 1);

    setHasBeenLiked(wasLiked);
  };

  const _onBookmarkedChanged = (bookmarked) => setBookmarked(bookmarked);

  const _onNutricionalTableClick = () => {
    nutricionalTableRef.current.scrollIntoView();
  };

  return (
    <Step
      spacing={10}
      onBackButtonClick={exitPage}
      stepTitle={title}
      headerButtons={
        isRecipeOwnedByUser ? (
          <Grid item>
            <IconButton className={classes.editButton} onClick={goToEditPage}>
              <Edit></Edit>
            </IconButton>
          </Grid>
        ) : showBookmarkIcon ? (
          <Grid item>
            <BookmarkButton
              recipeID={id}
              bookmarked={bookmarked}
              onDone={_onBookmarkedChanged}
            ></BookmarkButton>
          </Grid>
        ) : (
          <React.Fragment></React.Fragment>
        )
      }
      renderBody={() => {
        return (
          <Grid container direction="column">
            {coverPhotoURL && (
              <Grid item container justify="center">
                <PhotoCover coverPhotoURL={coverPhotoURL}></PhotoCover>
              </Grid>
            )}

            <Grid item container style={{ marginTop: coverPhotoURL ? 80 : 0 }}>
              <ExtraData
                timeToPrepare={timeToPrepare}
                servings={servings}
                onNutricionalTableClick={_onNutricionalTableClick}
                showNutricionalTableButton={!!nutricionalData.calories}
              ></ExtraData>
            </Grid>

            {description && (
              <Grid item container style={{ marginTop: 80 }}>
                <Grid item style={{ paddingLeft: 16, paddingRight: 16 }}>
                  <Description variant="h6">
                    <span className={classes.description}>{description}</span>
                  </Description>
                </Grid>
              </Grid>
            )}

            <Grid
              item
              container
              direction="column"
              spacing={4}
              style={{ marginTop: 80 }}
            >
              <Grid item>
                <Title>Ingredients</Title>
              </Grid>

              <Grid item>
                <IngredientsList ingredients={ingredients}></IngredientsList>
              </Grid>
            </Grid>

            <Grid
              item
              container
              direction="column"
              spacing={4}
              style={{ marginTop: 80 }}
            >
              <Grid item>
                <Title>Step by step</Title>
              </Grid>
              <Grid item>
                <DirectionsList directions={directions}></DirectionsList>
              </Grid>
            </Grid>

            {nutricionalData && nutricionalData.calories && (
              <Grid
                item
                container
                direction="column"
                spacing={4}
                style={{ marginTop: 80 }}
              >
                <Grid item>
                  <Title>Nutricional Table</Title>
                </Grid>

                <Grid item>
                  <NutricionalTable
                    nutricionalTableRef={nutricionalTableRef}
                    nutricionalData={nutricionalData}
                  ></NutricionalTable>
                </Grid>
              </Grid>
            )}

            <Grid item container direction="column" style={{ marginTop: 80 }}>
              <Grid item>
                <Likes
                  likes={likes}
                  hasBeenLiked={hasBeenLiked}
                  onLikedChanged={_onLikedChanged}
                  recipeID={id}
                  isOwnedByUser={isRecipeOwnedByUser}
                ></Likes>
              </Grid>

              <Grid item style={{ marginTop: 16, marginBottom: 16 }}>
                <Divider style={{ backgroundColor: colors.red[400] }}></Divider>
              </Grid>

              <Grid item container style={{ marginTop: 16 }}>
                <User userName={userName}></User>
              </Grid>
            </Grid>
          </Grid>
        );
      }}
    ></Step>
  );
};

const PhotoCover = ({ coverPhotoURL }) => {
  const isUpMD = useIsUpMD();
  const classes = useStyles();

  return (
    <img
      src={coverPhotoURL}
      alt="Recipe Cover"
      className={classes.coverPhoto}
      style={{ height: isUpMD ? 300 : 180, width: isUpMD ? 400 : 240 }}
    ></img>
  );
};

const useExtraDataStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  child: {
    padding: 8,
  },
  icon: {
    color: colors.red[400],
  },
  nutricionalTable: {
    textTransform: 'none',
    color: colors.red[400],
    textDecoration: 'underline',
    '&:hover': {
      backgroundColor: colors.red[100],
      textDecoration: 'underline',
    },
  },
});

const ExtraData = ({
  timeToPrepare,
  servings,
  onNutricionalTableClick,
  showNutricionalTableButton,
}) => {
  const isUpMD = useIsUpMD();
  const classes = useExtraDataStyles();

  return (
    <Grid container direction="column" alignItems="flex-end">
      <Grid item container spacing={4} justify="flex-end" alignItems="center">
        {isUpMD && (
          <Grid item className={classes.container}>
            <div className={classes.child}>
              <Description>
                <b>Preparation time</b>
              </Description>
            </div>

            {servings && (
              <div className={classes.child}>
                <Description>
                  <b>Serves</b>
                </Description>
              </div>
            )}
          </Grid>
        )}

        <Grid item className={classes.container}>
          <div className={classes.child}>
            <Description>
              <i>{`${timeToPrepare} min`}</i>
            </Description>
          </div>

          {servings && (
            <div className={classes.child}>
              <Description>
                <i>{`${servings} ${servings === 1 ? 'person' : 'persons'}`}</i>
              </Description>
            </div>
          )}
        </Grid>

        <Grid item className={classes.container}>
          <div className={classes.child}>
            <Icon className={classes.icon}>
              <AccessTime fontSize="small"></AccessTime>
            </Icon>
          </div>

          {servings && (
            <div className={classes.child}>
              <Icon className={classes.icon}>
                <People fontSize="small"></People>
              </Icon>
            </div>
          )}
        </Grid>
      </Grid>

      {showNutricionalTableButton && (
        <Grid item style={{ marginTop: 16 }}>
          <Button
            className={classes.nutricionalTable}
            onClick={onNutricionalTableClick}
          >
            <Description>
              <span style={{ fontFamily: 'Fredoka One, cursive' }}>
                Nutricional Table
              </span>
            </Description>
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const IngredientsList = ({ ingredients }) => {
  return (
    <Grid
      container
      direction="column"
      style={{ paddingLeft: 16, paddingRight: 16 }}
    >
      {ingredients.map((ingredient, index) => {
        return (
          <Grid
            item
            key={ingredient.id}
            style={{ marginTop: index > 0 ? 16 : 0 }}
          >
            <IngredientsCard
              ingredientUIModel={ingredient}
              renderButtons={() => <StyledCheckbox></StyledCheckbox>}
            ></IngredientsCard>
          </Grid>
        );
      })}
    </Grid>
  );
};

const DirectionsList = ({ directions }) => {
  return (
    <Grid
      container
      direction="column"
      style={{ paddingLeft: 16, paddingRight: 16 }}
    >
      {directions.map((direction, index) => {
        return (
          <Grid
            item
            key={direction.id}
            style={{ marginTop: index > 0 ? 16 : 0 }}
          >
            <DirectionCard
              directionUIModel={direction}
              index={index}
              renderButtons={() => <StyledCheckbox></StyledCheckbox>}
            ></DirectionCard>
          </Grid>
        );
      })}
    </Grid>
  );
};

const nutricionalTableStyles = makeStyles({
  calories: {
    fontFamily: 'Fredoka One, cursive',
    color: colors.red[400],
  },
  grams: {
    fontFamily: 'Fredoka One, cursive',
    color: colors.red[400],
  },
});

const NutricionalTable = ({ nutricionalData, nutricionalTableRef }) => {
  const { calories, protein, carbohydrate, fat } = nutricionalData;
  const classes = nutricionalTableStyles();

  return (
    <div ref={nutricionalTableRef}>
      <Grid
        container
        direction="column"
        style={{ paddingLeft: 16, paddingRight: 16 }}
      >
        <Grid item container spacing={6}>
          <Grid item>
            <Description>
              <b>Calories</b>
            </Description>
          </Grid>
          <Grid item>
            <Description>
              {`${calories}  `}
              <span className={classes.calories}>cal</span>
            </Description>
          </Grid>
        </Grid>

        <Grid item style={{ marginTop: 16, marginBottom: 16 }}>
          <Divider></Divider>
        </Grid>

        <Grid item container spacing={6}>
          <Grid item>
            <NutrientItem label="Protein" value={protein}></NutrientItem>
          </Grid>
          <Grid item>
            <NutrientItem
              label="Carbohydrate"
              value={carbohydrate}
            ></NutrientItem>
          </Grid>
          <Grid item>
            <NutrientItem label="Fat" value={fat}></NutrientItem>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
const NutrientItem = ({ label, value }) => {
  const classes = nutricionalTableStyles();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Description>
        <b>{label}</b>
      </Description>
      <Description>
        {`${value} `}
        <span className={classes.grams}>g</span>
      </Description>
    </div>
  );
};

const Likes = ({
  likes,
  hasBeenLiked,
  onLikedChanged,
  recipeID,
  isOwnedByUser,
}) => {
  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <LikeButton
          liked={hasBeenLiked}
          onDone={onLikedChanged}
          recipeID={recipeID}
          isOwnedByUser={isOwnedByUser}
        ></LikeButton>
      </Grid>

      <Grid item>
        <Description>{likes}</Description>
      </Grid>
    </Grid>
  );
};

const userStyles = makeStyles({
  userName: {
    color: colors.red[400],
    fontFamily: 'Pacifico,cursive',
  },
  sharedBy: {
    fontFamily: 'Fredoka One, cursive',
    color: 'rgba(0, 0, 0, 0.64)',
  },
});
const User = ({ userName }) => {
  const classes = userStyles();

  return (
    <Grid container direction="column">
      <Grid item>
        <Typography variant="h5">
          <span className={classes.sharedBy}>Shared by</span>
        </Typography>
      </Grid>
      <Grid item style={{ marginTop: 8 }}>
        <Typography variant="h4">
          <span className={classes.userName}>{userName}</span>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default RecipeLoaded;
